import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  input,
  OnInit,
  Output,
  signal,
  DestroyRef
} from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
// InputNumberModule removed in favor of shared CurrencyInputFieldComponent
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { TextareaModule } from '@openng/optimus-ui/textarea';
import { ButtonModule } from '@openng/optimus-ui/button';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import {
  CurrencyService,
  CurrencyInputFieldComponent,
  FileUploadService,
  FileUploadServiceV2,
  ImagePreview,
  ImagePreviewItem
} from '@zayan-erp/shared';
import { environment } from 'apps/cinari-erp-client/src/environments/environment';
import { DsmAddendumService } from '../../../services/dsm-addendum.service';
import { DsmCreateAddendumDepositPayload } from '../../../models/dsm-addendum-deposit.models';
import { finalize, map, switchMap, tap } from 'rxjs';
import { HttpEventType } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'dsm-aps-addendum-deposit-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DatePickerModule,
    CurrencyInputFieldComponent,
    InputTextModule,
    TextareaModule,
    ButtonModule,
    TooltipModule,
    ImagePreview
  ],
  templateUrl: './dsm-aps-addendum-deposit-form.component.html',
  styleUrls: []
})
export class DsmApsAddendumDepositFormComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly currencyService = inject(CurrencyService);

  readonly #fileUploadService = inject(FileUploadService);
  readonly #fileUploadServiceV2 = inject(FileUploadServiceV2);

  addendumId = input<string>('');
  // Deposit type ID loaded from environment configuration
  readonly depositTypeId = environment.depositTypeId;
  lotId = input<string>(''); // Lot ID from parent

  readonly currencyCode = this.currencyService.currencyCode;
  readonly currencySymbol = this.currencyService.currencySymbol;

  @Output() formSaved = new EventEmitter<void>();
  @Output() formCancel = new EventEmitter<void>();
  private readonly addendumService = inject(DsmAddendumService);
  attachments = signal<
    { file: File | null; name: string; s3Key: string; documentId: string; s3Url: string; progress: number | null }[]
  >([]);

  loadingAttachmentId = signal<string | null>(null);

  readonly #destroyRef = inject(DestroyRef);
  readonly #cdr = inject(ChangeDetectorRef);

  form!: FormGroup;

  constructor() {
    this.form = this.fb.group({
      depositAmount: [null as number | null, [Validators.required]],
      depositDueDate: [null as Date | null, [Validators.required]],
      paymentAmount: [null as number | null, [Validators.required]],
      paymentDate: [null as Date | null, [Validators.required]],
      notes: [''],
      referenceNo: ['']
    });
  }

  ngOnInit(): void {}

  onChequeFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      Array.from(input.files).forEach(file => this.#uploadFile(file));
    }
    input.value = '';
  }
  removeAttachment(attachment: {
    file: File | null;
    name: string;
    s3Key: string;
    documentId: string;
    s3Url: string;
    progress: number | null;
  }): void {
    this.attachments.update(list => list.filter(a => a !== attachment));
  }
  #uploadFile(file: File): void {
    this.attachments.update(list => [
      ...list,
      { file, name: file.name, s3Key: '', documentId: '', s3Url: '', progress: 0 }
    ]);

    this.#fileUploadService
      .getPrivateAWSSignedUrl(file)
      .pipe(
        switchMap(awsData =>
          this.#fileUploadService.uploadFileToS3(awsData).pipe(
            tap((uploadEvent: any) => {
              if (uploadEvent?.type === HttpEventType.UploadProgress && uploadEvent.total) {
                const progress = Math.round((100 * uploadEvent.loaded) / uploadEvent.total);
                this.attachments.update(list =>
                  list.map(a =>
                    a.file === file ? { ...a, progress, s3Key: awsData.s3Key, documentId: awsData.documentId } : a
                  )
                );
              }
            }),
            map(() => awsData)
          )
        ),
        switchMap(awsData => this.#fileUploadService.getPresignedUrlFromS3Key(awsData.s3Key, awsData.fileName)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(s3Url => {
        this.attachments.update(list => list.map(a => (a.file === file ? { ...a, s3Url, progress: null } : a)));
      });
  }
  getAttachmentImages(): ImagePreviewItem[] {
    return this.attachments()
      .filter(a => a.s3Url && a.progress === null)
      .map(a => ({ itemImageSrc: a.s3Url, thumbnailImageSrc: a.s3Url }));
  }

  getSavedAttachmentImages(attachment: { s3Url: string }): ImagePreviewItem[] {
    return attachment.s3Url ? [{ itemImageSrc: attachment.s3Url, thumbnailImageSrc: attachment.s3Url }] : [];
  }

  viewSavedAttachment(attachment: { documentId: string; s3Url: string }, preview: ImagePreview): void {
    if (attachment.s3Url) {
      preview.open();
      return;
    }

    this.loadingAttachmentId.set(attachment.documentId);
    this.#fileUploadServiceV2
      .getPresignedUrlFromS3DocumentId(attachment.documentId)
      .pipe(
        finalize(() => this.loadingAttachmentId.set(null)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(url => {
        this.attachments.update(list =>
          list.map(a => (a.documentId === attachment.documentId ? { ...a, s3Url: url } : a))
        );
        this.#cdr.detectChanges();
        preview.open();
      });
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const val = this.form.value;
    const paymentImageIds = this.attachments()
      .filter(a => a.documentId && a.progress === null)
      .map(a => a.documentId);

    const payload: DsmCreateAddendumDepositPayload = {
      depositTypeId: this.depositTypeId,
      lotId: this.lotId(),
      depositAmount: val.depositAmount,
      depositDueDate: val.depositDueDate instanceof Date ? val.depositDueDate.toISOString() : val.depositDueDate,
      paymentAmount: val.paymentAmount,
      paymentDate: val.paymentDate instanceof Date ? val.paymentDate.toISOString() : val.paymentDate,
      notes: val.notes || '',
      paymentReferenceNo: val.referenceNo || '',
      ...(paymentImageIds.length ? { paymentImageIds } : {})
    };

    const addendumId = this.addendumId();
    if (!addendumId) {
      return;
    }

    this.addendumService.createAddendumDeposit(addendumId, payload).subscribe({
      next: () => {
        this.formSaved.emit();
      }
    });
  }

  onCancel(): void {
    this.attachments.set([]);
    this.formCancel.emit();
  }
}
